/**
 * Town map: the municipal boundary polygon from Firestore plus one marker per
 * active listing.
 *
 * Compliance is encoded twice - hue AND marker shape - because red/green/amber
 * status hues alone are not separable under deuteranopia. The legend and the
 * table view carry the same classes, so colour never gates a value.
 *
 * Equirectangular projection with a cos(latitude) correction, which is exact
 * enough across a single municipality and needs no tile server or API key.
 */

import { svg, attachTip, hideTooltip } from './tooltip.js';
import { computeFrame, fitView, ringsOf } from './geo.js';
import { basemapUrl, MAX_REQUEST_PX } from './basemap.js';

const SURFACE = '#ffffff';

/** Distinct silhouettes, ordered by how much attention the class deserves. */
export const MARKER_SHAPES = {
  violation: 'triangle',
  needsAttention: 'diamond',
  deferred: 'ring',
  licensed: 'circle'
};

function markerPath(shape, x, y, r) {
  switch (shape) {
    case 'triangle': {
      const h = r * 1.9;
      return `M${x},${y - h * 0.62} L${x + h * 0.58},${y + h * 0.38} L${x - h * 0.58},${y + h * 0.38} Z`;
    }
    case 'diamond': {
      const d = r * 1.35;
      return `M${x},${y - d} L${x + d},${y} L${x},${y + d} L${x - d},${y} Z`;
    }
    default:
      return null; // circles and rings are drawn as <circle>
  }
}

export function renderMap(
  container,
  { boundary, points, categories, onFilterChange, onSelect, mapsKey = null }
) {
  while (container.firstChild) container.removeChild(container.firstChild);

  const rings = ringsOf(boundary);
  if (!points.length && !rings.length) {
    container.textContent = 'No geometry available.';
    return { setFilter: () => {} };
  }

  // Where the window sits, and why it is clamped: see ./geo.js.
  const frame = computeFrame(points, rings);

  // The coordinate space is the basemap image's own pixel grid, in Web
  // Mercator, so a marker lands on the roof it belongs to rather than beside
  // it. The SVG scales to the card through its viewBox, so this holds whether
  // or not an image is actually fetched.
  const view = fitView(frame, { maxPx: MAX_REQUEST_PX });
  const { width, height, px, py } = view;

  const root = svg('svg', {
    class: 'map-svg',
    viewBox: `0 0 ${width} ${height}`,
    role: 'img',
    'aria-label':
      'Map of active short-term rental listings over satellite imagery, ' +
      'coloured and shaped by compliance category'
  });
  container.appendChild(root);

  const defs = svg('defs');
  root.appendChild(defs);

  // --- satellite backdrop ---------------------------------------------------
  // Drawn first so everything else sits over it. A failed fetch leaves the
  // plain surface behind it: the town's listings never depend on Google being
  // reachable.
  const imageUrl = basemapUrl(view, mapsKey);
  if (imageUrl) {
    const img = svg('image', {
      href: imageUrl,
      x: 0, y: 0, width, height,
      preserveAspectRatio: 'none'
    });
    img.addEventListener('error', () => {
      img.remove();
      root.setAttribute('data-basemap', 'unavailable');
    });
    root.appendChild(img);
    root.setAttribute('data-basemap', 'google-hybrid');
  }

  // --- boundary, and the scrim that makes markers legible over imagery ------
  // The compliance palette was validated against a near-white surface
  // (README: the deuteranopia analysis). Aerial imagery is a busy mid-dark
  // one, which would void that. Rather than restyle four status colours, the
  // backdrop is washed back toward the surface the palette was chosen for -
  // heavier outside the town, so the municipality reads as the subject and
  // its neighbours as context.
  const ringPath = rings
    .map(r => r.map((c, i) => `${i ? 'L' : 'M'}${px(c[0]).toFixed(2)},${py(c[1]).toFixed(2)}`).join(' ') + ' Z')
    .join(' ');

  if (imageUrl && rings.length) {
    // evenodd: the canvas rect minus the town, i.e. everything outside it.
    root.appendChild(svg('path', {
      class: 'map-scrim-outside',
      'fill-rule': 'evenodd',
      d: `M0,0 L${width},0 L${width},${height} L0,${height} Z ${ringPath}`
    }));
    root.appendChild(svg('path', { class: 'map-scrim-inside', d: ringPath }));
  } else if (imageUrl) {
    // No boundary stored for this town: one even wash over the whole frame.
    root.appendChild(svg('rect', { class: 'map-scrim-inside', x: 0, y: 0, width, height }));
  }

  if (rings.length) {
    root.appendChild(svg('path', {
      class: imageUrl ? 'map-boundary map-boundary-over' : 'map-boundary',
      d: ringPath,
      'fill-rule': 'evenodd'
    }));
  }

  // --- markers -------------------------------------------------------------
  // Drawn least-urgent first so violations and unmatched listings sit on top.
  const drawOrder = ['licensed', 'deferred', 'needsAttention', 'violation'];
  const layer = svg('g', {});
  root.appendChild(layer);

  const byCategory = new Map(drawOrder.map(k => [k, []]));
  for (const p of points) {
    if (byCategory.has(p.category)) byCategory.get(p.category).push(p);
  }

  const nodes = [];

  for (const key of drawOrder) {
    const meta = categories[key];
    const group = svg('g', { 'data-category': key });
    layer.appendChild(group);

    for (const p of byCategory.get(key)) {
      const x = px(p.lng);
      const y = py(p.lat);
      const r = key === 'licensed' ? 3.4 : 5;
      const shape = MARKER_SHAPES[key];

      // Over imagery every mark carries a surface ring, not just the two
      // that always had one: a 3.4px dot in isolation on a white card is a
      // different problem from the same dot on a roof. On the plain surface
      // the filled circle keeps its original unringed look.
      const ringed = Boolean(imageUrl);

      let mark;
      if (shape === 'circle') {
        mark = svg('circle', {
          cx: x, cy: y, r: ringed ? r + 0.4 : r,
          fill: meta.color,
          'fill-opacity': ringed ? 1 : 0.85,
          ...(ringed ? { stroke: SURFACE, 'stroke-width': 1.5 } : {})
        });
      } else if (shape === 'ring') {
        mark = svg('circle', {
          cx: x, cy: y, r,
          fill: SURFACE, stroke: meta.color, 'stroke-width': 2.2
        });
      } else {
        mark = svg('path', {
          d: markerPath(shape, x, y, r),
          fill: meta.color,
          stroke: SURFACE,
          'stroke-width': 2,
          'stroke-linejoin': 'round'
        });
      }
      mark.setAttribute('class', 'map-point');
      group.appendChild(mark);

      // A 24px transparent hit target - the painted mark is far too small to aim at.
      const hit = svg('circle', {
        class: 'hit',
        cx: x, cy: y, r: 12,
        role: 'button',
        'aria-label': `${p.title || p.id}: ${meta.label}`
      });
      attachTip(hit, () => ({
        title: p.title || p.address || p.id,
        sub: [p.address, p.host ? `Host ${p.host}` : null].filter(Boolean).join(' · '),
        rows: [
          { label: '', value: meta.label, color: meta.color, shape: 'dot' },
          { label: 'on platform', value: p.source || 'unknown' },
          { label: 'days tracked', value: p.ageDays == null ? '--' : String(p.ageDays) }
        ]
      }));
      const select = () => {
        if (onSelect) onSelect(p);
        else if (/^https?:\/\//i.test(p.url || '')) window.open(p.url, '_blank', 'noopener');
      };
      hit.addEventListener('click', select);
      hit.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select();
        }
      });
      root.appendChild(hit);
      nodes.push({ key, mark, hit });
    }
  }

  function setFilter(active) {
    for (const n of nodes) {
      const on = !active || active === n.key;
      n.mark.setAttribute('opacity', on ? 1 : 0.06);
      n.hit.style.pointerEvents = on ? 'auto' : 'none';
      n.hit.setAttribute('tabindex', on ? '0' : '-1');
      n.hit.setAttribute('aria-hidden', on ? 'false' : 'true');
    }
    hideTooltip();
    if (onFilterChange) onFilterChange(active);
  }

  return { setFilter };
}
